import {Component, OnInit, inject} from '@angular/core';
import {Company} from '../../../_data/admin/company';
import {I18nService} from '../../../_services/gui/i18n.service';
import {CompaniesService} from '../../../_services/data/companies.service';
import {ActivatedRoute, ParamMap, Router, RouterLink} from '@angular/router';
import {faEdit, faTrash} from '@fortawesome/free-solid-svg-icons';
import {BehaviorSubject} from 'rxjs';
import {User} from '../../../_data/admin/user';
import {UsersService} from '../../../_services/data/users.service';
import {BranchesService} from '../../../_services/data/branches.service';
import {Branch} from '../../../_data/admin/branch';
import {containsIgnoreCase} from '../../../_utils/lang';
import {HttpResponse} from '@angular/common/http';
import {filter, map, tap} from 'rxjs/operators';
import {NotificationService} from '../../../_services/gui/notification.service';
import {ofOr, tapStatusToNotification} from '../../../_utils/operators';
import {getStateFromRouter} from '../../../_utils/nav';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {Location} from '@angular/common';
import {isCompany} from '../../../_utils/type-guards';
import {GuiPermissionsService} from '../../../_services/gui/gui-permissions.service';
import {IconButtonComponent} from '../../../common/icon-button/icon-button.component';
import {NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgbNavOutlet} from '@ng-bootstrap/ng-bootstrap/nav';
import {AriaAttrsParentDirective} from '../../../_directives/aria-attrs-parent.directive';
import {ItemListComponent} from '../../common/item-list/item-list.component';
import {SortableDirective} from '../../../_directives/sortable.directive';
import {UserListComponent} from '../../user-admin/user-list/user-list.component';
import {TranslateModule} from '@ngx-translate/core';

const TABS = ['', 'branches', 'admins', 'users'];

@Component({
    selector: 'app-company-details',
    templateUrl: './company-details.component.html',
    styleUrls: ['./company-details.component.scss'],
    imports: [IconButtonComponent, RouterLink, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, 
      AriaAttrsParentDirective, NgbNavContent, ItemListComponent, SortableDirective, UserListComponent, NgbNavOutlet, 
      TranslateModule]
})
export class CompanyDetailsComponent implements OnInit {

  readonly guiPermissionsService = inject(GuiPermissionsService);


  faEdit = faEdit as IconProp;
  faTrash = faTrash as IconProp;

  canEditCompany: boolean = false;
  canCreateCompanyAdmins: boolean = false;

  company: Company;
  companyNameParam: string;
  companyNotFound: boolean;

  administratorsSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  usersSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);

  branchesSubject: BehaviorSubject<Branch[]> = new BehaviorSubject<Branch[]>([]);
  filteredSortedBranches: Branch[];

  // Gets used directly by the tabs component.  Will initiate it and be updated by it.  1-based index.
  private _activeTabId: number;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly i18nService = inject(I18nService);
  private readonly companiesService = inject(CompaniesService);
  private readonly branchesService = inject(BranchesService);
  private readonly usersService = inject(UsersService);
  private readonly notificationService = inject(NotificationService);

  constructor() {
    const guiPermissionsService = this.guiPermissionsService;

    guiPermissionsService.canEditCompany$.subscribe(b => this.canEditCompany = b);
    guiPermissionsService.canAddCompanyAdmins$.subscribe(b => this.canCreateCompanyAdmins = b);

    //Initialize the active tab if it's in the url
    this._activeTabId = CompanyDetailsComponent.getTabIndex(this.route?.firstChild?.snapshot?.params?.tab);
    console.debug(`Incoming activeTabId from url: ${this._activeTabId}`);

    //Monitor the paramMap in case the param on the url doesn't match what we think the activated route should be.
    //This is meant to catch back/forward buttons changing the tabs
    this.route.firstChild?.paramMap.pipe(
      //Convert the incoming tab name to its index
      map((m: ParamMap) => CompanyDetailsComponent.getTabIndex(m.get('tab'))),
      //Only need to do stuff if it's a valid number and the tab is different than expected
      filter(t => !isNaN(t) && this._activeTabId !== t)
    ).subscribe(t => {
      this._activeTabId = t;
    });

    //Load the company data.  Either by the router data or the company name in the url.
    this.companyNameParam = this.route?.snapshot?.params?.companyName?.toUpperCase();
    const searchForCompanyObs = this.companiesService.getCompanyDetails(this.companyNameParam).pipe(
      tap(c => {
        if(c == null) {
          console.error('Company from API is null.  Does the company exist?');
          this.companyNotFound = true;
        }
      })
    );
    ofOr(getStateFromRouter(this.router), searchForCompanyObs, isCompany).subscribe(c => {
      console.debug(`Company loaded: ${JSON.stringify(c)}`);
      this.company = c;
      //After the company data is loaded, get its branches
      this.branchesService.getBranches(c.companyName).pipe(
        tapStatusToNotification(this.notificationService, [
          {statusCode: 204, i18nMessage: 'content.company-has-no-branches'},
          {statusCode: 200, i18nMessage: null}
        ])
      ).subscribe((httpResponse: HttpResponse<Branch[]>) => {
        if(httpResponse.status === 200) {
          this.branchesSubject.next(httpResponse.body);
        }
      });
      // Now that the company data is loaded, get the detailed data for its users
      this.refreshUsers();
    });
  }

  /**
   * Given a tab name find its 1-based index
   *
   * @param tabName - branches, admin, users
   * @private
   */
  private static getTabIndex(tabName: string): number {
    const i = TABS.indexOf(tabName || 'branches');
    //Tab index is 1-based, so -1 (not found) or 0 (blank) should default to the first tab (1).
    return i < 1 ? 1 : i;
  }

  ngOnInit() {
    this.i18nService.setTitle('title.admin.companies.detail');
  }

  delete(company: Company, ariaLabelledBy: string, container: HTMLElement) {
    this.companiesService.confirmDeleteCompany(company, ariaLabelledBy, container, (httpResponse) => {
      if(httpResponse.status === 200) {
        this.router.navigateByUrl('/admin/companies');
      }
    });
  }

  branchMatchesFilter(_filter: string, b: Branch) {
    return containsIgnoreCase(b.memberCode, _filter) || containsIgnoreCase(b.branchName, _filter) || containsIgnoreCase(b.product, _filter);
  }

  deleteBranch(branch: Branch, ariaLabelledBy: string, container: HTMLElement) {
    console.debug(`Deleting branch ${branch.memberCode}`);
    this.branchesService.confirmDeleteBranch(this.company.companyName, branch.branchName, ariaLabelledBy, container).subscribe(success => {
        if(success) {
          this.branchesSubject.next(this.branchesSubject.value.filter(b => b.branchName !== branch.branchName));
        }
      }
    );
  }

  /**
   * Refreshes the data that is displayed in the users/admins lists
   */
  refreshUsers(): void {
    // Get all users for a given company
    this.companiesService.getCompanyUsers(this.company.companyName).pipe(tap(users => {
      // Check if any users were returned for the company
      if([...users.users, ...users.admins].length === 0) {
        console.info(`Company ${this.company.companyName} has no users/admins!`);
      }
    })).subscribe(users => {
      // Provide the users and then the admins to any components (the user lists) that need them
      this.usersSubject.next(users.users);
      this.administratorsSubject.next(users.admins);
    });
  }

  get activeTabId(): number {
    return this._activeTabId;
  }

  set activeTabId(value: number) {
    this._activeTabId = value;
    //Put the tab index in the url so that forward/back buttons can work with the selected tab
    const tabName = TABS[value];
    this.router.navigate([`./${tabName || 'branches'}`], {relativeTo: this.route, state:{ignoreLoadingBar: true}});
  }
}
